import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { LoadingService } from "@coso/loading";
import { DialogComponent, DialogType, IS_SAAS } from "@coso/utility";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  AutomaticSharePointUserCreationMode,
  AutomaticSharePointUserCreationModeName,
  GroupStorage,
  IConfigurationHolder,
  MultipleLinkResolutionType,
  MultipleLinkResolutionTypeName,
  SdkMessageProcessingStepsRegistrationMode,
  SdkMessageProcessingStepsRegistrationModeName
} from "../../../models/auto-generated";
import { QuickActionsService } from "../../../services/quick-actions.service";
import { SettingsStore } from "../../../stores/settings.store";
import { handleError } from "../../../utils/handle-error";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";

@Component({
  standalone:false,
  templateUrl: "./behavior.component.html",
  styleUrls: ["./behavior.component.scss"]
})
export class BehaviorComponent implements OnInit, OnDestroy, AfterViewInit {
  private unsubscribe = new Subject<void>();
  @ViewChild(NgForm) public formRef: NgForm;
  AutomaticSharePointUserCreationMode = AutomaticSharePointUserCreationMode;
  MultipleLinkResolutionType = MultipleLinkResolutionType;
  GroupStorage = GroupStorage;
  SdkMessageProcessingStepsRegistrationMode = SdkMessageProcessingStepsRegistrationMode;
  errorMessage: string;
  successMessage: string;
  multipleLinkResolutionTypes: string[] = Object.keys(MultipleLinkResolutionType).filter((value) => isNaN(Number(value)));
  automaticSharePointUserCreationModes: string[] = Object.keys(AutomaticSharePointUserCreationMode).filter((value) => isNaN(Number(value)));
  sdkMessageProcessingStepsRegistrationModes = Object.keys(SdkMessageProcessingStepsRegistrationMode).filter((value) => isNaN(Number(value)));
  instanceId: string;
  settings$: Observable<IConfigurationHolder>;
  readonly$: Observable<boolean>;

  constructor(
    @Inject(IS_SAAS) public isSaaS: boolean,
    public loadingService: LoadingService,
    private settingsStore: SettingsStore,
    private modalService: NgbModal,
    private quickActionsService: QuickActionsService,
    private route: ActivatedRoute
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe.next();
  }

  ngAfterViewInit(): void {
    // more info: https://angular.io/errors/NG0100
    setTimeout(() => {
      this.formRef.form.markAllAsTouched();
      this.formRef.form.statusChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
        this.settingsStore.updateValidity("behavior", this.formRef.valid);
      });
    }, 0);
  }

  ngOnInit(): void {
    this.instanceId = this.route.parent.snapshot.paramMap.get("instance-id");
    this.settings$ = this.settingsStore.settings$;
    this.readonly$ = this.settingsStore.isReadOnly$;

    // Older configurations can miss this field; default it so the dropdown is not blank on first load.
    this.settings$.pipe(takeUntil(this.unsubscribe)).subscribe((settings) => {
      if (settings && settings.sdkMessageProcessingStepsRegistrationMode === undefined) {
        settings.sdkMessageProcessingStepsRegistrationMode = SdkMessageProcessingStepsRegistrationMode.UnmanagedCustomizations;
        this.settingsStore.updateSettings(settings);
      }
    });
  }

  clearBrokenLinkCache(): void {
    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"
    });
    modalRef.componentInstance.options = {
      titleText: "Clear broken link cache",
      type: DialogType.Warning,
      contentText: "Are you sure you want to clear broken link cache?",
      rightButtons: [
        { label: "No", type: "button", class: "btn btn-dark mr-2", onClick: () => modalRef.dismiss() },
        {
          label: "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: () => {
            this.successMessage = this.errorMessage = undefined;
            this.loadingService.start("Clearing cache");
            this.quickActionsService.clearBrokenLinkCache(this.instanceId).subscribe(
              () => {
                this.successMessage = "Broken link cache has been successfully cleared.";
                this.loadingService.stop();
                modalRef.dismiss();
              },
              (error) => {
                this.errorMessage = `Failed to clear broken link cache due to: ${handleError(error)}`;
                this.loadingService.stop();                
                modalRef.dismiss();
                
                setTimeout(() => {
                  const panel = document.getElementById('panel');
                  if (panel) {
                    panel.scrollTop = 0;
                  }
                }, 0);                                
              }
            );
          }
        }
      ],
      onClose: () => modalRef.dismiss()
    };
  }

  getMultipleLinkResolutionTypeName(type: string) {
    return MultipleLinkResolutionTypeName.get(MultipleLinkResolutionType[type]);
  }

  updateSettings(current: IConfigurationHolder) {
    this.settingsStore.updateSettings(current);
  }

  getAutomaticSharePointUserCreationModeName(mode: string) {
    return AutomaticSharePointUserCreationModeName.get(AutomaticSharePointUserCreationMode[mode]);
  }

  getSdkMessageProcessingStepsRegistrationModeName(mode: string) {
    return SdkMessageProcessingStepsRegistrationModeName.get(SdkMessageProcessingStepsRegistrationMode[mode]) || mode;
  }
}
